import { useState, useEffect, useRef } from 'react';
import type { GameCardLiveProps } from '../types/game-card';
import '../styles/game-badges.css';

const GameCardLive = ({ gameLive, game, onPlay }: GameCardLiveProps) => {
    const [dealerImageLoaded, setDealerImageLoaded] = useState(false);
    const [tableImageLoaded, setTableImageLoaded] = useState(false);
    const [, forceUpdate] = useState({});
    const touchStartRef = useRef<{ x: number; y: number } | null>(null);
    const touchMovedRef = useRef(false);

    // Auto update per l'immagine dinamica ogni 5 secondi
    useEffect(() => {
        const interval = setInterval(() => {
            forceUpdate({});
        }, 5000);
        
        return () => clearInterval(interval);
    }, []);

    const handleCardClick = () => {
        onPlay?.(game);
    };

    const handleTouchStart = (e: React.TouchEvent) => {
        const touch = e.touches[0];
        touchStartRef.current = { x: touch.clientX, y: touch.clientY };
        touchMovedRef.current = false;
    };

    const handleTouchMove = (e: React.TouchEvent) => {
        if (!touchStartRef.current) return;
        
        const touch = e.touches[0];
        const deltaX = Math.abs(touch.clientX - touchStartRef.current.x);
        const deltaY = Math.abs(touch.clientY - touchStartRef.current.y);
        
        // If user moved more than 10 pixels, consider it a scroll
        if (deltaX > 10 || deltaY > 10) {
            touchMovedRef.current = true;
        }
    };

    const handleTouchEnd = (e: React.TouchEvent) => {
        // Only trigger click if user didn't scroll
        if (!touchMovedRef.current && touchStartRef.current) {
            e.preventDefault();
            handleCardClick();
        }
        touchStartRef.current = null;
        touchMovedRef.current = false;
    };

    // Formatta l'importo da centesimi a euro
    const formatCentsToEuro = (cents: number) => {
        const euros = cents / 100;
        return euros.toLocaleString('it-IT', {
            minimumFractionDigits: 2,
            maximumFractionDigits: 2
        }) + '€';
    };

    // Ottieni il colore per i numeri della roulette
    const getRouletteColor = (number: number): string => {
        if (number === 0) return '#00A86B'; // Verde
        const redNumbers = [1, 3, 5, 7, 9, 12, 14, 16, 18, 19, 21, 23, 25, 27, 30, 32, 34, 36];
        return redNumbers.includes(number) ? '#DC143C' : '#2B2B2B'; // Rosso o Nero
    };

    // Calcola la dimensione dei numeri della roulette
    const getRouletteNumberSize = (index: number): number => {
        return index === 0 ? 24 : 20;
    };

    return (
        <button
            type="button"
            className="group relative w-[153px] flex-shrink-0 text-left p-0 bg-transparent border-0 block"
            onClick={handleCardClick}
            onTouchStart={handleTouchStart}
            onTouchMove={handleTouchMove}
            onTouchEnd={handleTouchEnd}
            style={{ WebkitTapHighlightColor: 'transparent', cursor: 'pointer' }}
        >
            {/* Container immagine con proporzioni fisse */}
            <div className="relative w-full h-[190px] rounded-lg overflow-hidden bg-gray-800 pointer-events-none">
                {/* Immagine del tavolo */}
                <img
                    key={`${gameLive.tableConfig.dynamicImageUrl}-${Date.now()}`}
                    src={gameLive.tableConfig.dynamicImageUrl}
                    alt={gameLive.tableConfig.tableName}
                    className={`w-full h-full object-cover transition-opacity duration-300 ${
                        tableImageLoaded ? 'opacity-100' : 'opacity-0'
                    }`}
                    onLoad={() => setTableImageLoaded(true)}
                    onError={() => setTableImageLoaded(false)}
                />

                {/* Nome dealer in alto */}
                {gameLive.dealer.dealerName && (
                    <div className="absolute top-0 left-0">
                        <div className="text-sm font-bold px-2 py-1 min-w-[50px] badge-dealer">
                            {gameLive.dealer.dealerName}
                        </div>
                    </div>
                )}

                {/* Contenitore bottom con dealer pic, exclusive, limiti e numeri */}
                <div className="absolute bottom-0 left-0 right-0 flex flex-col">
                    {/* Immagine dealer */}
                    {gameLive.dealer.dealerPictureUrl && (
                        <div className="relative w-1/2 h-[95px]">
                            <img
                                src={gameLive.dealer.dealerPictureUrl}
                                alt={gameLive.dealer.dealerName}
                                className={`w-full h-full object-cover ${
                                    dealerImageLoaded ? 'opacity-100' : 'opacity-0'
                                }`}
                                onLoad={() => setDealerImageLoaded(true)}
                                onError={() => setDealerImageLoaded(false)}
                            />
                        </div>
                    )}

                    {/* Badge Exclusive */}
                    {gameLive.tableConfig.dedicated && (
                        <div className="w-full h-6 flex items-center justify-center badge-exclusive">
                            <span className="text-xs font-bold uppercase">
                                ESCLUSIVA
                            </span>
                        </div>
                    )}

                    {/* Limiti del tavolo */}
                    {gameLive.limits && gameLive.limits[0]?.values?.[0] && (
                        <div className="w-full h-6 flex items-center justify-center badge-jackpot">
                            <span className="text-xs font-bold">
                                {formatCentsToEuro(gameLive.limits[0].values[0].min)} - {formatCentsToEuro(gameLive.limits[0].values[0].max)}
                            </span>
                        </div>
                    )}

                    {/* Ultimi numeri della roulette */}
                    {gameLive.round?.result?.lastNumbers && gameLive.round.result.lastNumbers.length > 0 && (
                        <div className="w-full h-7 flex items-center justify-center gap-1 px-1 badge-roulette">
                        
                            {gameLive.round.result.lastNumbers.slice(0, 6).map((number, index) => {
                                const size = getRouletteNumberSize(index);
                                return (
                                    <div
                                        key={index}
                                        className="rounded-full flex items-center justify-center text-white text-xs font-bold border border-white"
                                        style={{
                                            backgroundColor: getRouletteColor(number),
                                            width: `${size}px`,
                                            height: `${size}px`,
                                            fontSize: '12px'
                                        }}
                                    >
                                        {number}
                                    </div>
                                );
                            })}
                        </div>
                    )}
                </div>
            </div>

            {/* Informazioni del gioco */}
            <div className="game-info-container pointer-events-none">
                <h3 className="game-title uppercase" title={gameLive.tableConfig.tableName}>
                    {gameLive.tableConfig.tableName}
                </h3>
                <div className="game-subtitle">
                    {gameLive.tableConfig.language && (
                        <span className="uppercase">{gameLive.tableConfig.language}</span>
                    )}
                    {gameLive.tableConfig.language && gameLive.freeSeatsCount !== undefined && (
                        <span> | </span>
                    )}
                    {gameLive.freeSeatsCount !== undefined && (
                        <span>Posti liberi {gameLive.freeSeatsCount}</span>
                    )}
                </div>
            </div>
        </button>
    );
};

export default GameCardLive;